import { uiText, useUiLanguage } from '../../utils/uiTranslation';
import React, { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { loadRun, listRuns, readDocument, runAgent } from './agentApi';
import ShortlistDialog from './ShortlistDialog';
import TalentPosition from '../TalentSearch/TalentPosition';
import { matchLabel, matchDirection } from '../TalentSearch/matchLabel';
import QueryFeed, { queriesForRun } from './QueryFeed';
import './TalentAgent.css';

const stages = {
  clarified: ['Clarified your requirements', 'توضيح متطلباتك'],
  understanding: ['Understanding your requirements', 'فهم متطلباتك'],
  searching: ['Searching for profiles', 'البحث عن المرشحين'],
  details: ['Checking experience and qualifications', 'التحقق من الخبرات والمؤهلات'],
  companies: ['Checking relevant company context', 'مراجعة معلومات الشركات ذات الصلة'],
  comparing: ['Comparing candidates against your requirements', 'مقارنة المرشحين بمتطلباتك'],
};

const duration = (seconds) => `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
const PAGE_SIZE = 10;

function ColumnHelp({ label, text, tr }) {
  const id = useId();
  const popup = useRef(null);
  const toggle = (event) => {
    event.preventDefault();
    const panel = popup.current;
    if (!panel.togglePopover()) return;
    // The native popover stays above the expanded table's scrolling container
    // and handles outside clicks and Escape without another modal or portal.
    const anchor = event.currentTarget.getBoundingClientRect();
    const { width, height } = panel.getBoundingClientRect();
    panel.style.left = `${Math.max(12, Math.min(anchor.left + anchor.width / 2 - width / 2, window.innerWidth - width - 12))}px`;
    const top = anchor.bottom + 8 + height <= window.innerHeight - 12 ? anchor.bottom + 8 : anchor.top - height - 8;
    panel.style.top = `${Math.max(12, top)}px`;
  };
  return <>
    <button type="button" className="ta-info" popoverTarget={id} onClick={toggle}
      aria-label={`${tr('About', 'حول')} ${label}`} aria-describedby={id}>
      <i className="fa-solid fa-circle-info" aria-hidden="true" />
    </button>
    <div id={id} ref={popup} popover="auto" className="ta-help-popover" role="note" aria-label={uiText(label)}>
      <strong>{uiText(label)}</strong><p>{text}</p>
    </div>
  </>;
}

function Results({ rows, expanded, openProfile, selectedIds, toggleCandidate, togglePage, tr }) {
  useUiLanguage();
  const { language } = useLanguage();
  // Expand the whole row together and retain its state when changing pages.
  const [expandedRows, setExpandedRows] = useState(() => new Set());
  const toggleRow = (id) => setExpandedRows((previous) => {
    const next = new Set(previous); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const allSelected = rows.length > 0 && rows.every((row) => selectedIds.has(row.id));
  const someSelected = rows.some((row) => selectedIds.has(row.id));
  const scoreHelp = tr('We score each candidate out of 100 against your requirements, giving more weight to your priorities. Missing profile details can lower the score.',
    'نقيّم كل مرشح من 100 وفق متطلباتك، ونعطي أولوياتك وزناً أكبر. قد تنخفض الدرجة عندما تنقص معلومات الملف.');
  const criteriaHelp = tr('We show a tag when the candidate’s profile supports a requirement. A missing tag means we couldn’t confirm a full match.',
    'نعرض وسمًا عندما تدعم معلومات المرشح أحد متطلباتك. غياب الوسم يعني أننا لم نتمكن من تأكيد المطابقة الكاملة.');
  const analysisHelp = tr('We connect the candidate’s work history to your role and highlight relevant strengths or gaps. Expand the table to read more.',
    'نربط خبرة المرشح بمتطلبات الوظيفة ونبرز نقاط القوة أو النواقص ذات الصلة. وسّع الجدول لقراءة المزيد.');
  return <div className={expanded ? 'ta-table-scroll' : 'ta-table-main'} tabIndex={expanded ? 0 : undefined} aria-label={tr('Candidate comparison', 'مقارنة المرشحين')}>
    <table className={`ta-table ${expanded ? 'ta-table-expanded' : 'ta-table-compact'}`}>
      <colgroup><col className="ta-col-select" /><col className="ta-col-name" /><col className="ta-col-score" />
        {expanded && <col className="ta-col-position" />}<col className="ta-col-tags" /><col className="ta-col-analysis" /></colgroup>
      <caption className="ta-sr-only">{tr('Candidates ranked by evidence against your requirements', 'المرشحون مرتّبون حسب الأدلة المطابقة لمتطلباتك')}</caption>
      <thead><tr>
        <th scope="col" className="ta-select-cell"><input type="checkbox" className="ta-row-checkbox"
          aria-label={expanded ? tr('Select all candidates', 'تحديد جميع المرشحين') : tr('Select all on this page', 'تحديد جميع مرشحي هذه الصفحة')}
          ref={(element) => { if (element) element.indeterminate = someSelected && !allSelected; }}
          checked={allSelected} onChange={() => togglePage(rows, !allSelected)} /></th>
        <th scope="col">{tr('Name', 'الاسم')}</th>
        <th scope="col" className="ta-score-cell">{tr('Fit Score', 'درجة المطابقة')} <ColumnHelp label={tr('Fit Score', 'درجة المطابقة')} text={scoreHelp} tr={tr} /></th>
        {expanded && <th scope="col">{tr('Position & location', 'المنصب والموقع')}</th>}
        <th scope="col">{tr('Matching Criteria', 'المعايير المطابقة')} <ColumnHelp label={tr('Matching Criteria', 'المعايير المطابقة')} text={criteriaHelp} tr={tr} /></th>
        <th scope="col">{tr('Analysis', 'التحليل')} <ColumnHelp label={tr('Analysis', 'التحليل')} text={analysisHelp} tr={tr} /></th>
      </tr></thead>
      <tbody>{rows.map((row) => {
        const meta = row.profile.metadata;
        const name = meta.name || tr('View profile', 'عرض الملف');
        const summary = row.analysis?.summary?.text || [meta.current_role, meta.current_company].filter(Boolean).join(' · ') || '—';
        const rowOpen = expandedRows.has(row.id);
        const tags = expanded || rowOpen ? row.matched : row.matched.slice(0, 2);
        const tagPreview = <><span className="ta-tags" dir={tags.some(matchDirection) ? 'rtl' : undefined}>{tags.map((check) => <span className="ta-tag" key={check.criterion_id} title={matchLabel(check)}>
          <i className="fa-solid fa-check" aria-hidden="true" /><bdi dir="auto">{matchLabel(check)}</bdi>
        </span>)}</span>{!row.matched.length && <span className="ta-muted">{tr('No confirmed matches', 'لا توجد مطابقة مؤكدة')}</span>}
          {!expanded && !rowOpen && row.matched.length > 2 && <span className="ta-more-tags">+{row.matched.length - 2}</span>}</>;
        return <tr key={row.id} data-candidate-id={row.id} tabIndex={expanded ? undefined : 0}
          aria-expanded={expanded ? undefined : rowOpen}
          className={`${selectedIds.has(row.id) ? 'ta-row-selected' : ''} ${rowOpen ? 'ta-row-expanded' : ''}`}
          onClick={expanded ? undefined : (event) => {
            // Buttons handle their own action; selection and profile navigation
            // must not also expand or collapse the row through event bubbling.
            if (!event.target.closest('button, input, a, .ta-select-cell')) toggleRow(row.id);
          }}
          onKeyDown={expanded ? undefined : (event) => {
            if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
              event.preventDefault(); toggleRow(row.id);
            }
          }}>
          <td className="ta-select-cell"><input type="checkbox" className="ta-row-checkbox" aria-label={`${tr('Select', 'تحديد')} ${name}`}
            checked={selectedIds.has(row.id)} onChange={() => toggleCandidate(row.id)} /></td>
          <td className="ta-person"><span className="ta-rank">#{row.rank}</span><button onClick={() => openProfile(row.profile)}>{meta.name || tr('View profile', 'عرض الملف')}</button></td>
          <td className="ta-score-cell"><span className={`ta-score ${row.required_conflict ? 'ta-score-conflict' : ''}`}>{row.score ?? '—'}</span></td>
          {expanded && <td><TalentPosition metadata={meta} language={language} showCompany={false} empty={tr('Position unconfirmed', 'المنصب غير مؤكد')} /><small><bdi dir="auto">{meta.current_company}</bdi></small><small>{meta.location || tr('Location unconfirmed', 'الموقع غير مؤكد')}</small></td>}
          <td className="ta-tags-cell">{expanded ? tagPreview : <button className={`ta-cell-expand ta-tag-preview ${rowOpen ? 'is-expanded' : ''}`} onClick={() => toggleRow(row.id)}
            aria-expanded={rowOpen} aria-label={`${rowOpen ? tr('Collapse matching criteria for', 'طي المعايير المطابقة لـ') : tr('Show all matching criteria for', 'عرض جميع المعايير المطابقة لـ')} ${name}`}>{tagPreview}</button>}</td>
          <td className="ta-analysis">{!expanded ? <button className="ta-cell-expand" onClick={() => toggleRow(row.id)}
            aria-expanded={rowOpen} aria-label={`${rowOpen ? tr('Collapse analysis for', 'طي التحليل لـ') : tr('Expand analysis for', 'توسيع التحليل لـ')} ${name}`}>
            {row.analysis?.source === 'profile_summary' && <span className="ta-analysis-label">{tr('From the profile', 'من الملف')}</span>}
            <span className={`ta-analysis-preview ${rowOpen ? 'is-expanded' : ''}`}>{summary}</span>
          </button> : <>
            {row.analysis?.source === 'profile_summary' && <small className="ta-analysis-label">{tr('From the profile', 'من الملف')}</small>}
            <p>{summary}</p>
            {row.analysis && <>
              {row.analysis.experience?.text && <p>{row.analysis.experience.text}</p>}
              {row.analysis.considerations?.text && <p className="ta-analysis-caveat">{row.analysis.considerations.text}</p>}
            </>}
          </>}</td>
        </tr>;
      })}</tbody>
    </table>
  </div>;
}

export default function TalentAgent({ onOpenShortlists } = {}) {
  useUiLanguage();
  const { currentUser } = useAuth();
  const { language, isRTL } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const ar = language === 'ar';
  const tr = (en, arabic) => ar ? arabic : en;
  const [query, setQuery] = useState('');
  const [document, setDocument] = useState(null);
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [expandedInput, setExpandedInput] = useState(false);
  const [expandedTable, setExpandedTable] = useState(false);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyCursor, setHistoryCursor] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [selectedRun, setSelectedRun] = useState(undefined);
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [shortlistOpen, setShortlistOpen] = useState(false);
  const [shortlistNotice, setShortlistNotice] = useState('');
  const historyController = useRef(null);
  const controller = useRef(null);
  const uploadController = useRef(null);
  const fileInput = useRef(null);
  const dialog = useRef(null);
  const historyPanel = useRef(null);
  const storageKey = currentUser ? `talent-agent-run:${currentUser.uid}` : '';
  const sessionGet = (key) => { try { return sessionStorage.getItem(key); } catch { return null; } };
  const remember = (id) => { try { if (storageKey) sessionStorage.setItem(storageKey, id); } catch { /* Storage can be disabled. */ } };

  useEffect(() => {
    controller.current?.abort();
    setRun(null); setBusy(false); setError('');
    if (!storageKey) return undefined;
    const restore = new AbortController();
    let timer;
    const id = selectedRun === undefined ? (location.state?.searchState?.agentRunId || sessionGet(storageKey)) : selectedRun?.id;
    const poll = async () => {
      try {
        const state = await loadRun(id, restore.signal);
        setRun(state); setBusy(state.status === 'running');
        if (state.status === 'running') timer = setTimeout(poll, 5000);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      }
    };
    if (id) poll();
    return () => { restore.abort(); clearTimeout(timer); };
  }, [storageKey, location.state?.searchState?.agentRunId, selectedRun]);

  useEffect(() => {
    historyController.current?.abort(); setHistory([]); setHistoryCursor(null); setHistoryOpen(false);
  }, [storageKey]);

  useEffect(() => {
    if (!historyOpen) return undefined;
    const outside = (event) => { if (!historyPanel.current?.contains(event.target)) setHistoryOpen(false); };
    const escape = (event) => { if (event.key === 'Escape') setHistoryOpen(false); };
    window.document.addEventListener('pointerdown', outside);
    window.document.addEventListener('keydown', escape);
    return () => {
      window.document.removeEventListener('pointerdown', outside);
      window.document.removeEventListener('keydown', escape);
    };
  }, [historyOpen]);

  useEffect(() => () => { controller.current?.abort(); uploadController.current?.abort(); historyController.current?.abort(); }, []);
  useEffect(() => {
    if (!busy) return undefined;
    const start = Date.now();
    setElapsed(0);
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [busy]);
  useEffect(() => {
    if (expandedTable) dialog.current?.showModal();
    else dialog.current?.close();
  }, [expandedTable]);

  useEffect(() => {
    // Selection belongs to this result set, never a different saved search/account.
    setPage(0); setSelectedIds(new Set()); setShortlistOpen(false); setShortlistNotice('');
    setExpandedTable(false);
  }, [run?.run_id, currentUser?.uid]);

  const receive = (event) => {
    if (event.type === 'state') {
      setRun(event.state);
      if (event.state.status !== 'running') setBusy(false);
    } else if (event.type === 'progress') {
      setRun((previous) => ({ ...previous, events: [...(previous?.events || []).map((e) => ({ ...e, done: true })), event.event] }));
    }
  };

  const send = async (payload) => {
    controller.current?.abort();
    controller.current = new AbortController();
    setBusy(true); setError('');
    remember(payload.run_id);
    try {
      await runAgent(payload, controller.current.signal, receive);
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message);
    } finally { setBusy(false); }
  };

  const start = (event) => {
    event.preventDefault();
    if (busy || uploading || (!query.trim() && !document)) return;
    const id = crypto.randomUUID().replaceAll('-', '');
    // A refinement retains the original brief and explicit Q&A, while cached
    // profile facts/checks are reused by the backend when they remain applicable.
    const parentId = ['complete', 'partial'].includes(run?.status) ? run.run_id : null;
    const combined = query.trim();
    if (combined.length > 16000) { setError(tr('Please shorten the search description.', 'يرجى تقصير وصف البحث.')); return; }
    setRun({ run_id: id, query: combined, status: 'running', rows: [], events: [],
      query_history: [...(parentId ? queriesForRun(run) : []), { query: combined, document_name: document?.name || '' }],
    });
    setQuestionIndex(0); setAnswers({}); setNotes({});
    send({ run_id: id, parent_run_id: parentId, query: combined, language: ar ? 'ar' : 'en', document_text: document?.text || '', document_name: document?.name || '' });
    setQuery(''); setDocument(null);
  };

  const upload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    uploadController.current = new AbortController();
    setUploading(true); setError('');
    try {
      const result = await readDocument(file, uploadController.current.signal);
      setDocument({ name: file.name, text: result.text });
    } catch (err) { if (err.name !== 'AbortError') setError(err.message); }
    finally { setUploading(false); }
  };

  const submitAnswers = (skip = false) => {
    const filled = {};
    for (const q of run.questions) filled[q.id] = [...(answers[q.id] || []), ...(notes[q.id]?.trim() ? [notes[q.id].trim()] : [])];
    send({ run_id: run.run_id, answers: filled, skip_questions: skip, language: ar ? 'ar' : 'en' });
  };

  const openProfile = (profile) => navigate('/sourcing/profile', {
    state: { profile, startUnlock: false, searchState: { agentRunId: run.run_id, searchText: run.query } },
  });
  const question = run?.status === 'clarifying' ? run.questions[questionIndex] : null;
  const complete = ['complete', 'partial'].includes(run?.status);
  const emptyPaused = run?.status === 'partial' && !run.rows?.length;
  const allRows = run?.rows || [];
  const pageCount = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = allRows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);
  const selectedRows = allRows.filter((row) => selectedIds.has(row.id));
  const toggleCandidate = (id) => setSelectedIds((previous) => {
    const next = new Set(previous); if (next.has(id)) next.delete(id); else next.add(id); return next;
  });
  const togglePage = (rows, checked) => setSelectedIds((previous) => {
    const next = new Set(previous); rows.forEach((row) => checked ? next.add(row.id) : next.delete(row.id)); return next;
  });
  const selectionBar = <div className="ta-selection-bar">
    <span aria-live="polite">{selectedRows.length} {tr('selected', 'محدد')}</span>
    {selectedRows.length > 0 && <button className="ta-link" onClick={() => setSelectedIds(new Set())}>{tr('Clear selection', 'إلغاء التحديد')}</button>}
    {selectedRows.length > 0 && selectedRows.length < allRows.length && <button className="ta-link" onClick={() => setSelectedIds(new Set(allRows.map((row) => row.id)))}>{tr(`Select all ${allRows.length}`, `تحديد الكل (${allRows.length})`)}</button>}
    <button className="ta-outline ta-shortlist-trigger" disabled={!selectedRows.length} onClick={() => { setShortlistNotice(''); setShortlistOpen(true); }}><i className="fa-solid fa-list-check" aria-hidden="true" />{tr('Add to shortlist', 'أضف إلى قائمة مختصرة')}</button>
  </div>;
  const tableProps = { openProfile, selectedIds, toggleCandidate, togglePage, tr };
  const continueResearch = () => {
    if (busy || !run?.coverage?.can_continue) return;
    const parent = run.run_id;
    const id = crypto.randomUUID().replaceAll('-', '');
    setRun((previous) => ({ ...previous, run_id: id, status: 'running', events: [], error: '' }));
    send({ run_id: id, parent_run_id: parent, action: 'continue', language: ar ? 'ar' : 'en' });
  };
  const stop = () => {
    controller.current?.abort();
    setBusy(false); setRun((prev) => ({ ...prev, status: 'stopped' }));
    // Cancellation saves server-side progress; restore it before offering resume.
    if (run?.run_id) loadRun(run.run_id).then((saved) => {
      if (saved.status !== 'running') setRun(saved);
      else setError(tr('Research is stopping. Reopen research to load its saved progress.', 'جارٍ إيقاف البحث. أعد فتح البحث لتحميل التقدم المحفوظ.'));
    }).catch(() => setError(tr('Reopen research to load its saved progress.', 'أعد فتح البحث لتحميل التقدم المحفوظ.')));
  };
  const fetchHistory = async (before = null) => {
    historyController.current?.abort();
    const request = new AbortController();
    historyController.current = request;
    setHistoryLoading(true); setHistoryError('');
    try {
      const page = await listRuns(before, request.signal);
      if (request.signal.aborted) return;
      setHistory((previous) => before ? [...previous, ...page.runs] : page.runs);
      setHistoryCursor(page.next_cursor);
    } catch (err) {
      if (err.name !== 'AbortError') setHistoryError(tr('Could not load history. Please retry.', 'تعذر تحميل السجل. يرجى المحاولة مجدداً.'));
    } finally { if (!request.signal.aborted) setHistoryLoading(false); }
  };
  const openHistoryRun = (id) => {
    if (busy) return;
    remember(id); setSelectedRun({ id }); setHistoryOpen(false);
    setQuestionIndex(0); setAnswers({}); setNotes({}); setQuery(''); setDocument(null);
  };
  const reset = () => {
    setSelectedRun(null); setHistoryOpen(false);
    setRun(null); setError(''); setQuery(''); setDocument(null);
    try { sessionStorage.removeItem(storageKey); } catch { /* Optional browser persistence. */ }
  };

  return <section className={`talent-agent ${!run ? 'ta-starter' : ''}`} dir={isRTL ? 'rtl' : 'ltr'} aria-label={tr('Sourcing agent', 'وكيل البحث')}>
    <div className="ta-toolbar">
      <div className="ta-history-wrap" ref={historyPanel}><button className={`ta-outline ${historyOpen ? 'is-active' : ''}`} aria-expanded={historyOpen} aria-controls="ta-history" onClick={() => {
        setHistoryOpen(!historyOpen); if (!historyOpen) fetchHistory();
      }}><i className="fa-solid fa-clock-rotate-left" aria-hidden="true" />{tr('History', 'السجل')}</button>
    {historyOpen && <section className="ta-history" id="ta-history" aria-label={tr('Search history', 'سجل البحث')}>
      <div className="ta-history-heading"><div><h2>{tr('Previous searches', 'عمليات البحث السابقة')}</h2>
        <p>{tr('Pick up where you left off.', 'تابع من حيث توقفت.')}</p></div>
        <button className="ta-history-close" aria-label={tr('Close history', 'إغلاق السجل')} onClick={() => setHistoryOpen(false)}><i className="fa-solid fa-xmark" aria-hidden="true" /></button>
      </div>
      {historyError && <p role="alert">{historyError} <button className="ta-link" onClick={() => fetchHistory()}>{tr('Retry', 'إعادة المحاولة')}</button></p>}
      <ul>{history.map((item) => <li key={item.run_id}><button className={`ta-history-item ${item.run_id === run?.run_id ? 'is-current' : ''}`} disabled={busy} onClick={() => openHistoryRun(item.run_id)}>
        <i className="fa-solid fa-magnifying-glass ta-history-icon" aria-hidden="true" /><span className="ta-history-copy">
        <span className="ta-history-query" dir="auto">{queriesForRun(item).at(-1)?.query || item.document_name || tr('Attached requirements', 'المتطلبات المرفقة')}</span>
        <small>{new Date(item.updated_at * 1000).toLocaleDateString(ar ? 'ar' : 'en', { month: 'short', day: 'numeric' })} · {item.status === 'clarifying' ? tr('Awaiting answers', 'بانتظار الإجابات')
          : !item.result_count && item.status !== 'complete' ? tr('Search paused', 'البحث متوقف')
            : `${item.result_count} ${tr('results', 'نتائج')}`}</small></span>
        <i className={`fa-solid fa-chevron-${isRTL ? 'left' : 'right'} ta-history-arrow`} aria-hidden="true" />
      </button></li>)}</ul>
      {historyLoading ? <p role="status">{tr('Loading history…', 'تحميل السجل…')}</p> : <>
        {!history.length && !historyError && <p>{tr('Your searches will appear here.', 'ستظهر عمليات البحث هنا.')}</p>}
        {historyCursor && <button className="ta-link" onClick={() => fetchHistory(historyCursor)}>{tr('Load more', 'تحميل المزيد')}</button>}
      </>}
    </section>}</div>
      <div className="ta-toolbar-actions">{run && <button className="ta-outline" disabled={busy} onClick={reset}><i className="fa-solid fa-plus" aria-hidden="true" />{tr('New search', 'بحث جديد')}</button>}
      {busy && <button className="ta-outline" onClick={stop} disabled={!controller.current}>{tr('Stop', 'إيقاف')}</button>}
      {onOpenShortlists && <button type="button" aria-label={tr('View Shortlists', 'عرض القوائم المختصرة')} title={tr('View Shortlists', 'عرض القوائم المختصرة')}
        style={{ background: 'none', border: 0, color: 'inherit', fontSize: 30, padding: 0, lineHeight: 1 }}
        onClick={() => onOpenShortlists({ agentRunId: run?.run_id, sourcingMode: 'agent', searchText: run?.query || query })}>
        <i className="fa-solid fa-rectangle-list" aria-hidden="true" />
      </button>}</div>
    </div>
    {!run && <header className="ta-intro"><h1>{tr('Find the right people, for any role.', 'اعثر على الأشخاص المناسبين لكل دور.')}</h1>
      <p>{tr('Describe the role or attach a job description. Get a ranked shortlist with clear reasons behind every match.', 'صِف الدور أو أرفق الوصف الوظيفي لتحصل على قائمة مرتبة مع أسباب واضحة لكل مطابقة.')}</p></header>}
    {run && <QueryFeed run={run} tr={tr} />}
    {!!error && <div className="ta-error" role="alert">{uiText(error)}{run?.run_id && <button className="ta-link" onClick={async () => {
      try { const state = await loadRun(run.run_id); setRun(state); setError(''); } catch (err) { setError(err.message); }
    }}>{tr('Reopen research', 'إعادة فتح البحث')}</button>}</div>}
    {question && <div className="ta-questions"><div className="ta-question-intro"><i className="fa-solid fa-circle-question" aria-hidden="true" />
      <div><h2>{tr('A few details will help me find a better fit.', 'بعض التفاصيل ستساعدني في العثور على مرشحين أنسب.')}</h2>
        <ul><li>{question.reason}</li></ul></div></div>
      <form onSubmit={(event) => { event.preventDefault(); if (questionIndex + 1 < run.questions.length) setQuestionIndex((i) => i + 1); else submitAnswers(); }}>
        <fieldset><legend><span>{tr('Question', 'السؤال')} {questionIndex + 1} {tr('of', 'من')} {run.questions.length}</span>{question.question}</legend>
          <ul className="ta-options">{question.options.map((option) => <li key={option}><label>
            <input type={question.multiple ? 'checkbox' : 'radio'} name={question.id} checked={(answers[question.id] || []).includes(option)} onChange={(event) => setAnswers((prev) => ({ ...prev, [question.id]: question.multiple ? (event.target.checked ? [...(prev[question.id] || []), option] : (prev[question.id] || []).filter((v) => v !== option)) : [option] }))} />
            <span>{uiText(option)}</span></label></li>)}</ul>
          <textarea rows={2} aria-label={tr('Additional instructions', 'تعليمات إضافية')} placeholder={tr('Or type your instructions here…', 'أو اكتب تعليماتك هنا…')} value={notes[question.id] || ''} maxLength={1500} onChange={(event) => setNotes((prev) => ({ ...prev, [question.id]: event.target.value }))} />
        </fieldset>
        <div className="ta-question-actions"><button type="button" className="ta-link" onClick={() => submitAnswers(true)}>{tr('Continue with what I provided', 'المتابعة بالمعلومات المتوفرة')}</button>
          {questionIndex > 0 && <button type="button" className="ta-link" onClick={() => setQuestionIndex((i) => i - 1)}>{tr('Back', 'رجوع')}</button>}
          <button className="ta-primary" disabled={busy}>{questionIndex + 1 < run.questions.length ? tr('Next', 'التالي') : tr('Submit', 'إرسال')} <i className="fa-solid fa-arrow-right" aria-hidden="true" /></button></div>
      </form></div>}
    {busy && <div className="ta-progress" role="status" aria-live="polite"><ol>{(run?.events || []).map((event, i) => <li key={`${event.stage}-${i}`} className={`${event.done ? 'is-done' : 'is-active'} ${i === (run?.events?.length || 0) - 2 ? 'leads-to-active' : ''}`}>
      <span className="ta-progress-dot" /><span>{stages[event.stage]?.[ar ? 1 : 0] || event.label}</span>
    </li>)}</ol><small>{tr('Working', 'جارٍ العمل')} {duration(elapsed)}</small></div>}
    {run?.error && !error && !emptyPaused && <p className="ta-error" role="alert">{run.error}</p>}
    {complete && <div className="ta-results"><p className="ta-worked">{tr('Worked', 'استغرق البحث')} {duration(run.elapsed_seconds || 0)} · {run.coverage?.assessed ?? run.searched_count} {tr('profiles assessed', 'ملفات تم تقييمها')}</p><div className="ta-results-top"><div>
      <h2>{run.rows.length ? tr('Here are the profiles I found for you', 'إليك الملفات التي عثرت عليها') : emptyPaused
        ? tr('Search paused before results could load.', 'توقف البحث قبل تحميل النتائج.')
        : tr('No profiles found for these requirements.', 'لم نعثر على ملفات تطابق هذه المتطلبات.')}</h2>
      {emptyPaused && <p className="ta-empty-message">{tr('Your requirements are saved. Resume the search to try again.', 'حفظنا متطلباتك. استأنف البحث للمحاولة مجدداً.')}</p>}</div>
      {!!run.rows.length && <button className="ta-outline" onClick={() => setExpandedTable(true)}><i className="fa-solid fa-expand" aria-hidden="true" /> {tr('Expand Table', 'توسيع الجدول')}</button>}</div>
      {!!run.rows.length && <>
        {selectionBar}
        {shortlistNotice && <p role="status" className="ta-shortlist-notice">{shortlistNotice}</p>}
        <Results key={run.run_id} rows={pageRows} {...tableProps} />
        <nav className="ta-pagination" aria-label={tr('Candidate pages', 'صفحات المرشحين')}>
          <span>{currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, allRows.length)} {tr('of', 'من')} {allRows.length}</span>
          <button className="ta-outline" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)} aria-label={tr('Previous page', 'الصفحة السابقة')}><i className={`fa-solid fa-chevron-${isRTL ? 'right' : 'left'}`} aria-hidden="true" /></button>
          <label>{tr('Page', 'صفحة')} <select aria-label={tr('Page', 'صفحة')} value={currentPage} onChange={(event) => setPage(Number(event.target.value))}>
            {Array.from({ length: pageCount }, (_, index) => <option key={index} value={index}>{index + 1}</option>)}
          </select> {tr('of', 'من')} {pageCount}</label>
          <button className="ta-outline" disabled={currentPage === pageCount - 1} onClick={() => setPage(currentPage + 1)} aria-label={tr('Next page', 'الصفحة التالية')}><i className={`fa-solid fa-chevron-${isRTL ? 'left' : 'right'}`} aria-hidden="true" /></button>
        </nav>
      </>}
      {run.status === 'partial' && <div className="ta-results-status">
        {run.rows.length > 0 && run.coverage?.reason !== 'batch_limit' && <span role="status">{
          run.coverage?.unassessed > 0
            ? tr(`${run.coverage.unassessed} candidates still need scoring.`, `${run.coverage.unassessed} مرشحين ما زالوا بحاجة للتقييم.`)
            : run.coverage?.reason === 'profile_details_unavailable'
              ? tr('Some profiles have limited information.', 'بعض الملفات تحتوي على معلومات محدودة.')
              : tr(`Search paused after ${run.rows.length} ${run.rows.length === 1 ? 'candidate' : 'candidates'}.`, `توقف البحث بعد ${run.rows.length} مرشحاً.`)
        }</span>}
        {run.coverage?.can_continue && <button className="ta-outline" disabled={busy} onClick={continueResearch}>{
          run.coverage?.reason === 'batch_limit'
            ? tr('Find more candidates', 'اعثر على المزيد من المرشحين')
            : tr('Resume search', 'استئناف البحث')
        }</button>}
      </div>}
    </div>}
    {(!run || complete || ['failed', 'stopped'].includes(run?.status)) && <form className={`ta-composer ${expandedInput ? 'is-expanded' : ''}`} onSubmit={start}>
      <button type="button" className="ta-expand-input" aria-label={expandedInput ? tr('Collapse search box', 'تصغير مربع البحث') : tr('Expand search box', 'توسيع مربع البحث')} aria-expanded={expandedInput} onClick={() => setExpandedInput((v) => !v)}><i className={`fa-solid ${expandedInput ? 'fa-compress' : 'fa-expand'}`} aria-hidden="true" /></button>
      <textarea aria-label={tr('Describe the candidates you need', 'صف المرشحين المطلوبين')} placeholder={complete ? tr('Refine your results', 'حسّن نتائجك') : tr('Describe the talent profiles you’re looking for', 'صف المرشحين الذين تبحث عنهم')} value={query} maxLength={16000} onChange={(event) => setQuery(event.target.value)} />
      <div className="ta-composer-actions">{document && <span className="ta-attachment">{document.name}<button type="button" aria-label={tr('Remove attachment', 'إزالة المرفق')} onClick={() => setDocument(null)}>×</button></span>}
        <input ref={fileInput} type="file" accept=".pdf,.docx,.txt" hidden onChange={upload} />
        <button type="button" className="ta-outline" disabled={uploading} onClick={() => fileInput.current.click()}><i className="fa-solid fa-paperclip" aria-hidden="true" /> {uploading ? tr('Reading file…', 'قراءة الملف…') : tr('Attach file', 'إرفاق ملف')}</button>
        <button className="ta-primary" disabled={busy || uploading || (!query.trim() && !document)}>{tr('Search', 'بحث')} <i className="fa-solid fa-arrow-right" aria-hidden="true" /></button></div>
    </form>}
    <dialog ref={dialog} className="ta-expanded-dialog" onCancel={() => setExpandedTable(false)} aria-labelledby="ta-expanded-title" dir={isRTL ? 'rtl' : 'ltr'}>
      {expandedTable && run && <><div className="ta-dialog-header"><div><h2 id="ta-expanded-title">{tr('Compare candidates', 'مقارنة المرشحين')}</h2><p>{run.rows.length} {tr('profiles · shared scoring criteria', 'ملفات · معايير تقييم موحّدة')}</p></div>
        <button className="ta-outline" onClick={() => setExpandedTable(false)} autoFocus><i className="fa-solid fa-compress" aria-hidden="true" /> {tr('Collapse Table', 'تصغير الجدول')}</button></div>
        {selectionBar}
        {shortlistNotice && <p role="status" className="ta-shortlist-notice">{shortlistNotice}</p>}
        <Results rows={allRows} expanded {...tableProps} /></>}
    </dialog>
    {shortlistOpen && selectedRows.length > 0 && <ShortlistDialog key={currentUser?.uid} rows={selectedRows} searchContext={{ runId: run?.run_id, query: run?.query || query }} tr={tr} isRTL={isRTL}
      onClose={() => setShortlistOpen(false)} onSaved={(name, count) => {
        setShortlistOpen(false); setSelectedIds(new Set());
        setShortlistNotice(tr(`Saved ${count} ${count === 1 ? 'candidate' : 'candidates'} to “${name}”.`, `تم حفظ ${count} مرشحاً في «${name}».`));
      }} />}
  </section>;
}
